import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  InputAdornment,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CakeIcon from "@mui/icons-material/Cake";
import WorkIcon from "@mui/icons-material/Work";
import FavoriteIcon from "@mui/icons-material/Favorite";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { userAPI } from "../../api";
import { useAuth } from "../../context/AuthContext";

const ROLES = [
  { value: "admin", label: "Admin", color: "#FF6B35" },
  { value: "support_team", label: "Support Team", color: "#4472C4" },
  { value: "team_member", label: "Team Member", color: "#2E7D32" },
  { value: "qa_team", label: "QA Team", color: "#7B1FA2" },
];

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "team_member",
  status: "active",
  dob: "",
  joining_date: "",
  anniversary_date: "",
};

const fmt = (d) => {
  if (!d) return null;
  const p = d.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const roleInfo = (role) =>
  ROLES.find((r) => r.value === role) || { label: role, color: "#888" };

// Generates a strong random password (12 chars, mixed case + digit + symbol,
// guaranteed at least one of each category).
const generateStrongPassword = (length = 12) => {
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const symbols = "!@#$%&*?";
  const all = lower + upper + digits + symbols;

  const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];

  let pwd = [pick(lower), pick(upper), pick(digits), pick(symbols)];
  for (let i = pwd.length; i < length; i++) pwd.push(pick(all));

  // Shuffle so the guaranteed characters aren't always in the same spots
  for (let i = pwd.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pwd[i], pwd[j]] = [pwd[j], pwd[i]];
  }
  return pwd.join("");
};

const AvatarBig = ({ name, color, size = 52 }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      bgcolor: color + "25",
      color,
      fontWeight: 800,
      fontSize: size * 0.4,
    }}
  >
    {name?.charAt(0).toUpperCase()}
  </Avatar>
);

export default function UsersPage() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    setLoading(true);
    userAPI
      .getAll()
      .then((res) => setUsers(res.data))
      .catch(() => setError("Failed to load users"))
      .finally(() => setLoading(false));
  };

  const openAdd = () => {
    setEditUser(null);
    setForm(emptyForm);
    setShowPassword(false);
    setOpenForm(true);
  };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      status: u.status,
      dob: u.dob || "",
      joining_date: u.joining_date || "",
      anniversary_date: u.anniversary_date || "",
    });
    setShowPassword(false);
    setOpenForm(true);
  };

  const handleGeneratePassword = () => {
    const pwd = generateStrongPassword(12);
    setForm((f) => ({ ...f, password: pwd }));
    setShowPassword(true);
  };

  const handleCopyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable; silently ignore
    }
  };

  const handleUnblock = async (id) => {
    try {
      await userAPI.unblock(id);
      setSuccess("User unblocked successfully");
      loadUsers();
    } catch {
      setError("Failed to unblock user");
    }
  };

  const handleSubmit = async () => {
    setError("");
    try {
      if (editUser) {
        const p = {
          name: form.name,
          role: form.role,
          status: form.status,
          dob: form.dob || null,
          joining_date: form.joining_date || null,
          anniversary_date: form.anniversary_date || null,
        };
        if (form.password) p.password = form.password;
        await userAPI.update(editUser.id, p);
        setSuccess("User updated successfully");
      } else {
        if (!form.password) return setError("Password is required");
        await userAPI.create({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          dob: form.dob || null,
          joining_date: form.joining_date || null,
          anniversary_date: form.anniversary_date || null,
        });
        setSuccess("User created successfully");
      }
      setOpenForm(false);
      setEditUser(null);
      setForm(emptyForm);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save user");
    }
  };

  const filtered = users.filter(
    (u) =>
      (!roleFilter || u.role === roleFilter) &&
      (u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())),
  );

  // Summary counts
  const counts = ROLES.reduce((acc, r) => {
    acc[r.value] = users.filter((u) => u.role === r.value).length;
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Team Members
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {users.length} registered users across {ROLES.length} roles
          </Typography>
        </Box>
        {isAdmin() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ px: 3 }}
          >
            Add User
          </Button>
        )}
      </Box>

      {/* Role summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {ROLES.map((r) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={2.4}
            key={r.value}
            sx={{ maxWidth: { xs: "50%", sm: "unset" } }}
          >
            <Paper
              onClick={() =>
                setRoleFilter(roleFilter === r.value ? "" : r.value)
              }
              sx={{
                p: 2,
                borderRadius: 3,
                cursor: "pointer",
                border: "2px solid",
                textAlign: "center",
                borderColor: roleFilter === r.value ? r.color : "transparent",
                bgcolor:
                  roleFilter === r.value ? r.color + "10" : "background.paper",
                transition: "all 0.15s",
                "&:hover": { borderColor: r.color, bgcolor: r.color + "08" },
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: r.color, lineHeight: 1 }}
              >
                {counts[r.value] || 0}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              >
                {r.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: "16px !important" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((u) => {
            const ri = roleInfo(u.role);
            return (
              <Grid item xs={12} sm={6} md={4} key={u.id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.15s, box-shadow 0.15s",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Top row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <AvatarBig name={u.name} color={ri.color} size={48} />
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, lineHeight: 1.2 }}
                          >
                            {u.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.3,
                            }}
                          >
                            <EmailIcon sx={{ fontSize: 11 }} />
                            {u.email}
                          </Typography>
                        </Box>
                      </Box>
                      {isAdmin() && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(u)}
                            sx={{
                              bgcolor: "#1E3A5F10",
                              "&:hover": { bgcolor: "#1E3A5F20" },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Role + Status */}
                    <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
                      <Chip
                        label={ri.label}
                        size="small"
                        sx={{
                          bgcolor: ri.color + "18",
                          color: ri.color,
                          fontWeight: 700,
                          fontSize: "0.65rem",
                        }}
                      />
                      <Chip
                        label={u.status}
                        size="small"
                        sx={{
                          bgcolor:
                            u.status === "active" ? "#2E7D3218" : "#88888818",
                          color: u.status === "active" ? "#2E7D32" : "#888",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          textTransform: "capitalize",
                        }}
                      />
                    </Box>

                    {u.block_task === 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "#FEE2E2",
                          border: "1px solid #FECACA",
                        }}
                      >
                        <WarningIcon
                          sx={{ fontSize: 16, color: "#DC2626", flexShrink: 0 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: "#DC2626", flex: 1 }}
                        >
                          Blocked — missed 3 days status
                        </Typography>
                        {isAdmin() && (
                          <Tooltip title="Unblock user">
                            <IconButton
                              size="small"
                              onClick={() => handleUnblock(u.id)}
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: "#DC2626",
                                color: "#fff",
                                "&:hover": { bgcolor: "#B91C1C" },
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    <Divider sx={{ mb: 2 }} />

                    {/* Date fields */}
                    <Stack spacing={1}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            bgcolor: "#FF6B3515",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CakeIcon sx={{ fontSize: 16, color: "#FF6B35" }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Date of Birth
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.2 }}
                          >
                            {fmt(u.dob) || (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            bgcolor: "#4472C415",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <WorkIcon sx={{ fontSize: 16, color: "#4472C4" }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Joining Date
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.2 }}
                          >
                            {fmt(u.joining_date) || (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            bgcolor: "#E9173515",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <FavoriteIcon
                            sx={{ fontSize: 16, color: "#E91735" }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Anniversary
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.2 }}
                          >
                            {fmt(u.anniversary_date) || (
                              <span style={{ color: "#bbb" }}>—</span>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>

                    {/* Last login */}
                    <Box
                      sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Last login:{" "}
                        {u.last_login
                          ? new Date(u.last_login).toLocaleString()
                          : "Never"}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {filtered.length === 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 10,
                  bgcolor: "background.paper",
                  borderRadius: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                }}
              >
                <PersonIcon
                  sx={{
                    fontSize: 56,
                    opacity: 0.15,
                    display: "block",
                    mx: "auto",
                    mb: 1,
                  }}
                />
                <Typography color="text.secondary">No users found</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Add / Edit Dialog */}
      <Dialog
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setEditUser(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {/* Colored top bar (radius removed - Paper's overflow:hidden clips this
            to match its own rounded corners, so the two no longer mismatch) */}
        <Box
          sx={{
            height: 6,
            background: "linear-gradient(90deg, #1E3A5F, #2E75B6, #4FC3F7)",
          }}
        />

        <DialogTitle sx={{ pt: 3, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {editUser ? (
              <AvatarBig
                name={editUser.name}
                color={roleInfo(editUser.role).color}
                size={44}
              />
            ) : (
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: "#1E3A5F20",
                  color: "#1E3A5F",
                }}
              >
                <PersonIcon />
              </Avatar>
            )}
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                {editUser ? "Edit User" : "Add New User"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editUser ? editUser.email : "Fill in the details below"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 , marginTop: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ marginTop: 1 }}>
            {/* Name */}
            <TextField
              fullWidth
              required
              label="Full Name"
              size="small"
              autoComplete="off"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            {/* Email (only on create) */}
            {!editUser && (
              <TextField
                fullWidth
                required
                label="Email Address"
                type="email"
                size="small"
                autoComplete="off"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Password */}
            <Box>
              <TextField
                fullWidth
                required={!editUser}
                label={
                  editUser ? "New Password (leave blank to keep)" : "Password"
                }
                type={showPassword ? "text" : "password"}
                size="small"
                name="new-user-password"
                autoComplete="new-password"
                inputProps={{ autoComplete: "new-password" }}
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon
                        sx={{ fontSize: 18, color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy password">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleCopyPassword}
                            disabled={!form.password}
                            edge="end"
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 0.75,
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                  onClick={handleGeneratePassword}
                  sx={{ px: 1, py: 0.25, minWidth: 0, fontSize: "0.75rem" }}
                >
                  Generate strong password
                </Button>
                {copied && (
                  <Typography
                    variant="caption"
                    sx={{ color: "success.main", fontWeight: 600 }}
                  >
                    Copied!
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Role + Status */}
            <TextField
              fullWidth
              select
              label="Role"
              size="small"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: r.color,
                        flexShrink: 0,
                      }}
                    />
                    {r.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            {editUser && (
              <TextField
                fullWidth
                select
                label="Status"
                size="small"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <MenuItem value="active">🟢 Active</MenuItem>
                <MenuItem value="inactive">🔴 Inactive</MenuItem>
              </TextField>
            )}

            {/* Personal dates section */}
            <Box sx={{ bgcolor: "background.default", borderRadius: 2, p: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                  letterSpacing: "0.08em",
                  display: "block",
                  mb: 1.5,
                }}
              >
                Personal Details (Optional)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={4}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    size="small"
                    value={form.dob}
                    onChange={(e) => setForm({ ...form, dob: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CakeIcon sx={{ fontSize: 16, color: "#FF6B35" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Joining Date"
                    type="date"
                    size="small"
                    value={form.joining_date}
                    onChange={(e) =>
                      setForm({ ...form, joining_date: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkIcon sx={{ fontSize: 16, color: "#4472C4" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Anniversary"
                    type="date"
                    size="small"
                    value={form.anniversary_date}
                    onChange={(e) =>
                      setForm({ ...form, anniversary_date: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FavoriteIcon
                            sx={{ fontSize: 16, color: "#E91735" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenForm(false);
              setEditUser(null);
            }}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !form.name ||
              (!editUser && (!form.email || !form.password))
            }
            sx={{ flex: 2 }}
          >
            {editUser ? "Save Changes" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}